import React from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import Navbar from "components/navbar";
import Sidebar from "components/sidebar";
import Footer from "components/footer/Footer";
import routes from "routes";
import { applyLanguage, getStoredLanguage } from "lib/language";
import { useAppLanguage } from "hooks/useAppLanguage";
import type { AuthGuard } from "services/authStorage";
import { instructorAuthService } from "services/instructorAuthService";
import { instructorNotificationsService } from "services/instructorNotificationsService";

type PortalLayoutProps = {
  authGuard: AuthGuard;
  sidebarLayout: "/admin" | "/instructor";
  fallbackTitleAr: string;
  fallbackTitleEn: string;
};

const isDesktopViewport = () =>
  typeof window !== "undefined" ? window.innerWidth >= 1200 : true;

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function routeMatches(pathname: string, layout: string, routePath: string): boolean {
  const segments = `${layout}/${routePath}`.replace(/\/+/g, "/").split("/");
  const pattern = segments
    .map((segment) => {
      if (segment === "*") return ".*";
      if (segment.startsWith(":")) return "[^/]+";
      return escapeRegExp(segment);
    })
    .join("\\/");

  return new RegExp(`^${pattern}(?:\\/|$)`).test(pathname);
}

export default function PortalLayout({
  authGuard,
  sidebarLayout,
  fallbackTitleAr,
  fallbackTitleEn,
}: PortalLayoutProps) {
  const location = useLocation();
  const language = useAppLanguage();
  const isArabic = language === "ar";
  const [open, setOpen] = React.useState(() => isDesktopViewport());

  React.useEffect(() => {
    const handleResize = () => setOpen(isDesktopViewport());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  React.useEffect(() => {
    if (!isDesktopViewport()) setOpen(false);
  }, [location.pathname]);

  React.useEffect(() => {
    applyLanguage(getStoredLanguage());
  }, []);

  const portalRoutes = routes.filter((route) => route.layout === sidebarLayout);
  const activeRoute = portalRoutes.find((route) =>
    routeMatches(location.pathname, route.layout, route.path)
  );
  const currentRoute = activeRoute
    ? isArabic
      ? activeRoute.nameAr || activeRoute.name
      : activeRoute.nameEn || activeRoute.name
    : isArabic
      ? fallbackTitleAr
      : fallbackTitleEn;

  return (
    <div className="apple-portal min-h-screen w-full">
      <Sidebar open={open} onClose={() => setOpen(false)} sidebarLayout={sidebarLayout} />

      <main
        className={`min-h-screen w-full transition-[padding] duration-300 ${
          isArabic ? "xl:pr-[304px]" : "xl:pl-[304px]"
        }`}
      >
        <div className="relative mx-auto min-h-screen max-w-[1760px] px-2 sm:px-4 xl:px-5">
          <Navbar
            onOpenSidenav={() => setOpen(true)}
            brandText={currentRoute}
            authGuard={authGuard}
            profileService={instructorAuthService}
            notificationsService={
              authGuard === "instructor" ? instructorNotificationsService : undefined
            }
          />

          <div
            className={`min-h-[calc(100vh-7rem)] py-4 sm:py-5 xl:py-6 ${
              isArabic ? "xl:pl-1" : "xl:pr-1"
            }`}
          >
            <Routes>
              {portalRoutes.map((route, index) => (
                <Route path={route.path} element={route.component} key={`${route.path}-${index}`} />
              ))}
              <Route index element={<Navigate to="default" replace />} />
              <Route path="*" element={<Navigate to="default" replace />} />
            </Routes>
          </div>

          <Footer />
        </div>
      </main>
    </div>
  );
}
